// src/components/tour/tours/siteManager.jsx
//
// Site Manager tour (internal admins). ctx is registered in
// SiteManager.jsx. The tour selects a real site for the detail steps and
// restores whatever was selected before when it ends.

import React from 'react';
import { replayStep } from './common';

export function getSiteManagerSteps(ctx, env) {
    const {
        sites = [],
        tourSite,
        tourClientName,
        unassignedCount = 0,
        matchCount = 0,
        selectTourSite,
    } = ctx;
    const { isMobile } = env;
    const click = isMobile ? 'Tap' : 'Click';

    const unlinked = sites.filter((s) => !s.client_id).length;
    const devCount = tourSite ? (tourSite.devices || []).length : 0;

    return [
        {
            target: null,
            title: 'Welcome to Site Manager',
            content: (
                <>
                    <p>
                        A <b>site</b> is a yacht, an event or any other location. Devices belong to a site,
                        and a client sees every device on the sites linked to them.
                    </p>
                    <p>
                        You have <span className="pt-data">{sites.length}</span> sites
                        {unlinked > 0 && <> (<span className="pt-data">{unlinked}</span> without a client)</>} and{' '}
                        <span className="pt-data">{unassignedCount}</span> unassigned device{unassignedCount === 1 ? '' : 's'}.
                    </p>
                </>
            ),
        },

        {
            when: () => matchCount > 0,
            target: '[data-tour="sm-matches"]',
            title: 'Billing matches',
            content: (
                <p>
                    <span className="pt-data">{matchCount}</span> site{matchCount === 1 ? '' : 's'} without a billing yacht look like a
                    yacht in billing with a similar name. <b>APPROVE</b> links them; <b>DISMISS</b> if it's the wrong match.
                </p>
            ),
        },

        {
            target: '[data-tour="sm-sites"]',
            title: 'Sites',
            content: (
                <p>
                    <b>+ NEW</b> creates a site (name and type: Yacht, Event or Other). Use the filter to find one,
                    and {click.toLowerCase()} a site to manage it on the {isMobile ? 'panel below' : 'right'}.
                </p>
            ),
        },

        {
            target: '[data-tour="sm-site"]',
            title: tourSite ? tourSite.site_name : 'A site',
            content: tourSite ? (
                <>
                    <p>
                        <span className="pt-data">{devCount}</span> device{devCount === 1 ? '' : 's'}
                        {tourSite.client_id ? <>, linked to <span className="pt-data">{tourClientName || 'a client'}</span></> : ', no client yet'}
                        {tourSite.supabase_yacht_id ? ', with billing linked' : ''}.
                    </p>
                    <p>
                        A small dot before a name means there are unassigned devices that probably belong
                        to that site. <b>EDIT</b> renames it.
                    </p>
                </>
            ) : null,
        },

        {
            target: '[data-tour="sm-client"]',
            onEnter: selectTourSite,
            title: 'Client access',
            content: (
                <p>
                    Choose which client owns this site. Linking gives that client every device on the site,
                    now and any added later. Pick “no client” to remove their access.
                </p>
            ),
        },

        {
            target: '[data-tour="sm-billing"]',
            onEnter: selectTourSite,
            title: 'Billing yacht',
            content: (
                <p>
                    Link the matching yacht from billing. The client's Starlink page then shows their plan and
                    boosters from that yacht's paid invoices. Without it, they see usage with no plan limit.
                </p>
            ),
        },

        {
            target: '[data-tour="sm-devices"]',
            onEnter: selectTourSite,
            title: 'Devices on this site',
            content: (
                <p>
                    The Peplink routers and Starlink kits assigned here. <b>REMOVE</b> sends one back to Unassigned.
                    Below, <b>Suggested</b> lists devices that probably belong here (a Starlink kit connected through
                    one of this site's routers, or a matching name).
                </p>
            ),
        },

        {
            target: '[data-tour="sm-unassigned"]',
            onEnter: selectTourSite,
            title: 'Unassigned devices',
            content: (
                <p>
                    Devices not on any site yet. With a site selected, <b>ADD</b> assigns one to it and{' '}
                    <b>ADD ALL</b> assigns every device in the list (respecting your search).
                </p>
            ),
        },

        replayStep,
    ];
}
